import React, { useEffect, useMemo, useState } from 'react';
import { Button, Grid, Header, Icon, Label, Loader, Message, Modal, Segment } from 'semantic-ui-react';
import {
  ContractListDto,
  ContractStatusHistoryDto,
  contractService,
} from '../../services/contracts/ContractService';
import { useAuth } from '../../context/AuthContext';
import { getContractStatusText } from '../../utils/contractStatusUtils';
import {
  getContractTypeLabel,
  isAditivoContract,
  isRenegociacaoContract,
  sortContractsForVtmeGroupOrder,
} from '../../utils/contractGroupVtmeOrder';

interface GroupStatusHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupContracts: ContractListDto[];
}

type HistoryState = {
  loading: boolean;
  error: string | null;
  history: ContractStatusHistoryDto[];
};

const emptyHistoryState: HistoryState = {
  loading: false,
  error: null,
  history: [],
};

const calculateDuration = (current: string, next?: string) => {
  try {
    const start = new Date(current);
    const end = next ? new Date(next) : new Date();
    const diffMs = end.getTime() - start.getTime();
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}min`;
    return `${minutes}min`;
  } catch {
    return '-';
  }
};

const formatDateTime = (dateString: string) => {
  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} às ${hours}:${minutes}`;
  } catch {
    return dateString;
  }
};

const StatusHistoryTimeline: React.FC<{
  history: ContractStatusHistoryDto[];
  isAdmin: boolean;
}> = ({ history, isAdmin }) => {
  if (history.length === 0) {
    return (
      <Message info>
        <Icon name="info circle" />
        Nenhum histórico de status disponível.
      </Message>
    );
  }

  return (
    <div style={{ position: 'relative', paddingLeft: '32px' }}>
      <div
        style={{
          position: 'absolute',
          left: '14px',
          top: 0,
          bottom: 0,
          width: '2px',
          background: 'linear-gradient(to bottom, #2ecc71, #3498db, #95a5a6)',
        }}
      />

      {history.map((item, index) => (
        <div
          key={item.id}
          style={{
            position: 'relative',
            marginBottom: '20px',
            paddingBottom: '16px',
            borderBottom: index < history.length - 1 ? '1px solid #eee' : 'none',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: '-25px',
              top: '2px',
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: index === 0 ? '#2ecc71' : '#3498db',
              border: '3px solid white',
              boxShadow: '0 0 0 1px #ddd',
              zIndex: 1,
            }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Header size="small" style={{ margin: 0 }}>
              {getContractStatusText(item.status)}
            </Header>
            {index === 0 && (
              <Label color="green" size="mini">
                <Icon name="check" />
                Status Atual
              </Label>
            )}
          </div>

          <div style={{ color: '#666', fontSize: '0.9em', marginBottom: '6px' }}>
            <Icon name="calendar" />
            {formatDateTime(item.changedAt)}
            {index < history.length - 1 && (
              <Label size="mini" style={{ marginLeft: '8px' }} color="grey">
                <Icon name="clock outline" />
                Duração: {calculateDuration(item.changedAt, history[index - 1]?.changedAt)}
              </Label>
            )}
            {index === 0 && (
              <Label size="mini" style={{ marginLeft: '8px' }} color="green">
                <Icon name="clock outline" />
                há {calculateDuration(item.changedAt)}
              </Label>
            )}
          </div>

          {isAdmin && item.changedByUserName && (
            <div style={{ color: '#666', fontSize: '0.9em', marginBottom: '6px' }}>
              <Icon name="user" />
              <strong>{item.changedByUserName}</strong>
              {item.changedByUserEmail && (
                <span style={{ color: '#999', marginLeft: '6px' }}>({item.changedByUserEmail})</span>
              )}
            </div>
          )}

          {!isAdmin && (
            <div style={{ color: '#999', fontSize: '0.85em', marginBottom: '6px' }}>
              <Icon name="user outline" />
              Alterado por: Sistema/Usuário
            </div>
          )}

          {item.changeReason && (
            <div
              style={{
                color: '#555',
                fontSize: '0.85em',
                fontStyle: 'italic',
                marginTop: '6px',
                padding: '8px 12px',
                background: '#f9f9f9',
                borderRadius: '4px',
                borderLeft: '3px solid #3498db',
              }}
            >
              <Icon name="info circle" color="blue" />
              {item.changeReason}
            </div>
          )}

          {item.groupId && (
            <div style={{ color: '#888', fontSize: '0.8em', marginTop: '6px' }}>
              <Icon name="sitemap" />
              <span style={{ marginLeft: '4px' }}>GroupId: {item.groupId}</span>
            </div>
          )}

          {item.previousStatus != null && (
            <div style={{ color: '#999', fontSize: '0.8em', marginTop: '8px' }}>
              <Icon name="arrow left" />
              Anterior: {getContractStatusText(item.previousStatus ?? undefined)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const GroupStatusHistoryModal: React.FC<GroupStatusHistoryModalProps> = ({
  isOpen,
  onClose,
  groupContracts,
}) => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('Admin');
  const [historyByContractId, setHistoryByContractId] = useState<Record<string, HistoryState>>({});

  const columns = useMemo(() => {
    const sorted = sortContractsForVtmeGroupOrder(groupContracts);
    const aditivo = sorted.find(isAditivoContract) ?? sorted[0] ?? null;
    const renegociacao =
      sorted.find(isRenegociacaoContract) ?? sorted.find((contract) => contract.id !== aditivo?.id) ?? null;

    return [
      { key: 'aditivo', title: 'Aditivo', contract: aditivo },
      { key: 'renegociacao', title: 'Renegociação', contract: renegociacao },
    ];
  }, [groupContracts]);

  const contractIds = columns.map((column) => column.contract?.id ?? '').join('|');

  useEffect(() => {
    if (!isOpen) return;

    const contractsToLoad = columns
      .map((column) => column.contract)
      .filter((contract): contract is ContractListDto => Boolean(contract));

    if (contractsToLoad.length === 0) {
      setHistoryByContractId({});
      return;
    }

    setHistoryByContractId(
      contractsToLoad.reduce<Record<string, HistoryState>>((acc, contract) => {
        acc[contract.id] = { ...emptyHistoryState, loading: true };
        return acc;
      }, {})
    );

    let cancelled = false;

    contractsToLoad.forEach(async (contract) => {
      try {
        const history = await contractService.getContractStatusHistory(contract.id);
        if (cancelled) return;
        setHistoryByContractId((prev) => ({
          ...prev,
          [contract.id]: { loading: false, error: null, history },
        }));
      } catch (err: any) {
        if (cancelled) return;
        setHistoryByContractId((prev) => ({
          ...prev,
          [contract.id]: {
            loading: false,
            error: err?.message || 'Erro ao carregar histórico',
            history: [],
          },
        }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, contractIds]);

  return (
    <Modal open={isOpen} onClose={onClose} size="large" closeIcon>
      <Modal.Header>
        <Icon name="history" />
        Histórico Completo de Status do Grupo
      </Modal.Header>

      <Modal.Content scrolling>
        <Grid columns={2} divided stackable>
          {columns.map(({ key, title, contract }) => {
            const state = contract ? historyByContractId[contract.id] ?? emptyHistoryState : emptyHistoryState;

            return (
              <Grid.Column key={key}>
                <Segment style={{ minHeight: '420px' }}>
                  <Header size="medium">
                    <Icon name={key === 'aditivo' ? 'plus circle' : 'refresh'} />
                    <Header.Content>
                      {title}
                      {contract && (
                        <Header.Subheader>
                          {getContractTypeLabel(contract)} · ID {contract.id}
                        </Header.Subheader>
                      )}
                    </Header.Content>
                  </Header>

                  {!contract ? (
                    <Message info>
                      <Icon name="info circle" />
                      Nenhum contrato de {title} neste grupo.
                    </Message>
                  ) : state.loading ? (
                    <div style={{ padding: '32px', textAlign: 'center' }}>
                      <Loader active inline />
                      <div style={{ marginTop: '12px', color: '#666' }}>Carregando histórico...</div>
                    </div>
                  ) : state.error ? (
                    <Message negative>
                      <Icon name="warning circle" />
                      {state.error}
                    </Message>
                  ) : (
                    <StatusHistoryTimeline history={state.history} isAdmin={isAdmin} />
                  )}
                </Segment>
              </Grid.Column>
            );
          })}
        </Grid>
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={onClose} primary>
          <Icon name="check" />
          Fechar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default GroupStatusHistoryModal;
